import type { Round } from '@h2h/shared';
import { ArrowRight, Trophy } from '../../../components/ui/icons';
import { cn } from '../../../lib/cn';
import { signedPoints } from '../../../lib/format';
import { StageChipPager } from './StageChipPager';
import { stagePhaseTone, type StagePhaseTone } from './helpers';
import { stageUserContext, type StageCtaKind } from './stageUserContext';

/**
 * Stage header — pager + title block + optional score and one CTA. Matches hub card chrome.
 */
export function StagePanel({
  rounds,
  selectedRoundId,
  currentRoundId,
  round,
  isCurrentStage,
  pickCount,
  picksRequired,
  resolvedCount,
  totalDuels,
  roundScore,
  recap,
  stageLive,
  entryEditable,
  onSelectRound,
  onCta
}: {
  rounds: Round[];
  selectedRoundId: string | null;
  currentRoundId: string | null;
  round: Round | undefined;
  isCurrentStage: boolean;
  pickCount: number;
  picksRequired: number;
  resolvedCount: number;
  totalDuels: number;
  roundScore: number | null | undefined;
  recap: { won: number; resolved: number; pending: number; points: number; sweep: boolean } | null;
  stageLive: boolean;
  entryEditable: boolean;
  onSelectRound: (roundId: string) => void;
  onCta: (kind: StageCtaKind) => void;
}) {
  const phase: StagePhaseTone = stagePhaseTone(round?.status);

  const ctx = stageUserContext({
    round,
    isCurrentStage,
    pickCount,
    picksRequired,
    resolvedCount,
    totalDuels,
    entryEditable
  });

  const showScore =
    recap != null &&
    (recap.resolved > 0 || (stageLive && recap.pending > 0) || phase === 'settled');
  const inProgress = stageLive && recap != null && recap.pending > 0;
  const progress =
    recap && recap.resolved + recap.pending > 0
      ? Math.round((recap.resolved / (recap.resolved + recap.pending)) * 100)
      : 0;

  return (
    <div className="mt-3 overflow-hidden rounded-[26px] border border-black/[0.08] bg-white shadow-[0_24px_50px_-28px_rgba(20,20,50,0.36)]">
      <StageChipPager
        rounds={rounds}
        selectedId={selectedRoundId}
        currentRoundId={currentRoundId}
        onSelect={onSelectRound}
      />

      {(showScore || ctx?.ctaLabel) && (
        <div className="border-t border-black/[0.06] px-[18px] pb-4 pt-3">
          {showScore && recap && (
            <StageScoreBlock
              recap={recap}
              roundScore={roundScore}
              inProgress={inProgress}
              progress={progress}
              sweep={!!recap.sweep && showScore}
            />
          )}
          {ctx?.ctaLabel && ctx.ctaKind && (
            <div className={cn(showScore && recap ? 'mt-3' : 'mt-0')}>
              <button
                type="button"
                onClick={() => onCta(ctx.ctaKind!)}
                className="tap flex w-full items-center justify-center gap-2 rounded-[17px] bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] p-[14px] shadow-[0_18px_34px_-16px_rgba(11,160,120,0.7)]"
              >
                <span className="text-[14px] font-extrabold tracking-[-0.01em] text-white">{ctx.ctaLabel}</span>
                <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-white" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function StageScoreBlock({
  recap,
  roundScore,
  inProgress,
  progress,
  sweep
}: {
  recap: { won: number; resolved: number; pending: number; points: number };
  roundScore: number | null | undefined;
  inProgress: boolean;
  progress: number;
  sweep: boolean;
}) {
  if (sweep) {
    return (
      <div className="relative mt-4 overflow-hidden rounded-[16px] bg-[linear-gradient(135deg,#2EDB7B_0%,#10B981_55%,#0BA078_100%)] px-3.5 py-3">
        <div className="relative flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Trophy width={16} height={16} strokeWidth={2.2} className="text-white" />
            <div>
              <div className="num text-[15px] font-extrabold leading-none text-white">
                {recap.won}/{recap.resolved} perfect
              </div>
              <div className="mt-0.5 text-[10px] font-semibold text-white/75">Flawless stage</div>
            </div>
          </div>
          <div className="num text-[17px] font-extrabold text-white">{signedPoints(recap.points)}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mt-4 flex items-center justify-between gap-3 rounded-[16px] px-3.5 py-3',
        inProgress ? 'bg-[rgba(227,168,43,0.08)]' : 'bg-[#F6F6F9]'
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="num text-[18px] font-extrabold leading-none tracking-[-0.02em] text-ink">
            {recap.won}/{recap.resolved}
          </span>
          <span className="text-[11px] font-semibold text-ink3">{inProgress ? 'won so far' : 'duels won'}</span>
        </div>
        {inProgress && (
          <div className="mt-2 h-[4px] overflow-hidden rounded-full bg-black/[0.08]">
            <div className="h-full rounded-full bg-[#E3A82B] transition-[width] duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      <div className="text-right">
        <div className="num text-[18px] font-extrabold leading-none text-ink">
          {signedPoints(roundScore ?? recap.points)}
        </div>
        <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-ink3">pts</div>
      </div>
    </div>
  );
}
